import StatusChip from 'components/@extended/StatusChip';
import PermissionButton from 'components/buttons/PermissionButton';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { CasePeopleRates } from 'pages/pricelists/CasePeopleRates';
import { FollowedPricelistLine, followedPricelistName } from 'pages/pricelists/FollowedPricelistLine';
import { inForceListOn, tabLabel } from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { useToday } from 'pages/pricelists/modal/useEmployeePricing';
import { formatRateRange } from 'pages/pricelists/rateRange';
import { useState } from 'react';
import { useDispatch } from 'store';
import { APIMattersSlice, useUpdateMatterPricingLinkMutation } from 'store/api/matters';
import { MatterDto } from 'types/matters';
import { MatterFollowedPricelist, MatterPricelist } from 'types/pricelists';
import { t } from 'utils/getTranslations';

interface Props {
    matter: Pick<MatterDto, 'id' | 'openedDate' | 'pricelistCurrencyTypeCode' | 'followsPricelist'>;
    // Every list of the case in date order, the base list first.
    lists: MatterPricelist[];
    // Edit and following another list: the case edit right, while the case's prices are not locked by its state.
    canEdit: boolean;
    // An own copy prices nothing differently, so the edit right alone allows it, in every case state.
    canMakeOwnCopy: boolean;
    onEdit: () => void;
    onSaved: () => void;
}

/**
 * An employee-priced case's prices on its Pricing section: the list it follows, read-only, or its own lists, and
 * the people of the list in force today. Following and the own copy are the case's link, changed from here.
 */
export function CaseEmployeePricing({ matter, lists, canEdit, canMakeOwnCopy, onEdit, onSaved }: Props): JSX.Element {
    const dateFormat = useEffectiveDateFormat();
    const today = useToday();
    const dispatch = useDispatch();
    const { openModal, closeModal } = useModal();
    const [updatePricingLink, { isLoading: isMakingCopy }] = useUpdateMatterPricingLinkMutation();
    const [activeKey, setActiveKey] = useState<number | undefined>(undefined);
    const followed = matter.followsPricelist ?? lists[0]?.followsPricelist ?? null;
    const openedDate = matter.openedDate ?? null;
    const currency = matter.pricelistCurrencyTypeCode;
    const inForce = inForceListOn(lists, today) ?? lists[0];
    const shown = (followed ? undefined : lists.find((list) => list.id === activeKey)) ?? inForce;
    const next = lists.find((list) => list.validFrom !== null && list.validFrom > today);
    const formatDate = (date: string) => format(parseISO(date), dateFormat);
    const inForceFrom = inForce?.validFrom ?? openedDate;
    const range = inForce
        ? formatRateRange(inForce.minHourlyPrice, inForce.maxHourlyPrice, inForce.avgHourlyPrice, currency)
        : '';

    const makeOwnCopy = async () => {
        await updatePricingLink({ id: String(matter.id), followsPricelist: null }).unwrap();
        dispatch(APIMattersSlice.util.invalidateTags([{ type: 'MatterRates', id: String(matter.id) }]));
        onSaved();
    };

    // Nothing is priced again, but the case drops the followed list's later periods and stops picking up its changes.
    const confirmOwnCopy = (followedList: MatterFollowedPricelist) => {
        const name = followedPricelistName(followedList);
        openModal(
            <ConfirmationModal
                titleId="pricelists.case-follow.make-own-copy-title"
                titleValues={{ name }}
                confirmId="pricelists.case-follow.make-own-copy"
                content={t('pricelists.case-follow.make-own-copy-body', { name })}
                onCancel={closeModal}
                onConfirm={() => {
                    closeModal();
                    void makeOwnCopy();
                }}
            />,
            closeModal
        );
    };

    return (
        <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                <div className="flex min-w-0 flex-[1_1_14rem] flex-col gap-1">
                    {followed ? (
                        <FollowedPricelistLine followed={followed} />
                    ) : (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{t('pricelists.case-follow.own-list')}</span>
                            <StatusChip type="neutral" label={t('pricelists.employee.per-employee')} />
                        </div>
                    )}
                    <div className="text-grey-600 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                        {range ? <span className="tabular-nums">{range}</span> : null}
                        {inForceFrom ? (
                            <span>
                                {t('pricelists.case-follow.period-in-force-from', { date: formatDate(inForceFrom) })}
                            </span>
                        ) : null}
                        {next?.validFrom ? (
                            <StatusChip
                                type="info"
                                size="small"
                                label={t('pricelists.case-follow.next-period', { date: formatDate(next.validFrom) })}
                            />
                        ) : null}
                    </div>
                </div>
                <div role="group" className="flex flex-wrap gap-2">
                    {followed ? (
                        <PermissionButton
                            hasAuthority={canMakeOwnCopy}
                            variant="outlined"
                            size="extraSmall"
                            disabled={isMakingCopy}
                            onClick={() => confirmOwnCopy(followed)}
                        >
                            {t('pricelists.case-follow.make-own-copy')}
                        </PermissionButton>
                    ) : (
                        <PermissionButton hasAuthority={canEdit} variant="outlined" size="extraSmall" onClick={onEdit}>
                            {t('edit')}
                        </PermissionButton>
                    )}
                    <PermissionButton hasAuthority={canEdit} variant="text" size="extraSmall" onClick={onEdit}>
                        {t(followed ? 'pricelists.case-follow.follow-another' : 'pricelists.case-follow.follow-a-list')}
                    </PermissionButton>
                </div>
            </div>
            {!followed && lists.length > 1 && shown ? (
                <LineTabs value={shown.id} onValueChange={(key) => setActiveKey(Number(key))}>
                    <LineTabsList arrows={{ back: t('back'), next: t('next') }}>
                        {lists.map((list) => (
                            <LineTab key={list.id} value={list.id} size="compact">
                                {tabLabel(
                                    { isBase: list.validFrom === null, validFrom: list.validFrom, name: list.name },
                                    openedDate,
                                    formatDate,
                                    (date) => t('pricelists.case-editor.from-date', { date })
                                )}
                            </LineTab>
                        ))}
                    </LineTabsList>
                </LineTabs>
            ) : null}
            <CasePeopleRates rows={shown?.personalRates ?? []} currencyTypeCode={currency} />
            <p className="text-grey-500 text-xs text-pretty">
                {t(followed ? 'pricelists.case-follow.follower-hint' : 'pricelists.case-follow.own-hint')}
            </p>
        </div>
    );
}
